// Othello rules (suunnitelma.md section 42). Pure functions used by the server (authoritative),
// the computer opponent and the UI (legal move hints).

export const OTHELLO_SIZE = 8;
/** Columns a–h (x = 0–7), rows 1–8 (y = 0–7, top to bottom). */
export const OTHELLO_COLUMNS = 'abcdefgh';

export type Disc = 'b' | 'w';
/** 64 cells, index = y * 8 + x. */
export type OthelloBoard = (Disc | null)[];

export interface OthelloCell {
  x: number;
  y: number;
}

const DIRECTIONS: readonly [number, number][] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

export const idx = (x: number, y: number) => y * OTHELLO_SIZE + x;
export const opponentOf = (c: Disc): Disc => (c === 'b' ? 'w' : 'b');
const onBoard = (x: number, y: number) => x >= 0 && y >= 0 && x < OTHELLO_SIZE && y < OTHELLO_SIZE;

export function othelloLabel(cell: OthelloCell): string {
  return `${OTHELLO_COLUMNS[cell.x]}${cell.y + 1}`;
}

/** Standard start: d4 and e5 white, e4 and d5 black. */
export function initialBoard(): OthelloBoard {
  const board: OthelloBoard = Array(OTHELLO_SIZE * OTHELLO_SIZE).fill(null);
  board[idx(3, 3)] = 'w';
  board[idx(4, 4)] = 'w';
  board[idx(4, 3)] = 'b';
  board[idx(3, 4)] = 'b';
  return board;
}

/** Discs flipped by placing `color` at (x, y); empty when the move is illegal. */
export function flipsFor(board: OthelloBoard, color: Disc, x: number, y: number): OthelloCell[] {
  if (!Number.isInteger(x) || !Number.isInteger(y) || !onBoard(x, y) || board[idx(x, y)] !== null) return [];
  const other = opponentOf(color);
  const flips: OthelloCell[] = [];
  for (const [dx, dy] of DIRECTIONS) {
    const line: OthelloCell[] = [];
    let cx = x + dx;
    let cy = y + dy;
    while (onBoard(cx, cy) && board[idx(cx, cy)] === other) {
      line.push({ x: cx, y: cy });
      cx += dx;
      cy += dy;
    }
    if (line.length > 0 && onBoard(cx, cy) && board[idx(cx, cy)] === color) flips.push(...line);
  }
  return flips;
}

export function legalMoves(board: OthelloBoard, color: Disc): OthelloCell[] {
  const moves: OthelloCell[] = [];
  for (let y = 0; y < OTHELLO_SIZE; y++) {
    for (let x = 0; x < OTHELLO_SIZE; x++) if (flipsFor(board, color, x, y).length > 0) moves.push({ x, y });
  }
  return moves;
}

/** Returns the new board, or null for an illegal move. */
export function applyMove(board: OthelloBoard, color: Disc, x: number, y: number): OthelloBoard | null {
  const flips = flipsFor(board, color, x, y);
  if (flips.length === 0) return null;
  const next = [...board];
  next[idx(x, y)] = color;
  for (const f of flips) next[idx(f.x, f.y)] = color;
  return next;
}

export function countDiscs(board: OthelloBoard): { b: number; w: number } {
  let b = 0;
  let w = 0;
  for (const c of board) {
    if (c === 'b') b++;
    else if (c === 'w') w++;
  }
  return { b, w };
}

/**
 * Who moves after `justMoved` has moved: the opponent if they have a legal move, otherwise the
 * same player again (the opponent passes), or null when neither can move (game over).
 */
export function nextToMove(board: OthelloBoard, justMoved: Disc): Disc | null {
  const other = opponentOf(justMoved);
  if (legalMoves(board, other).length > 0) return other;
  if (legalMoves(board, justMoved).length > 0) return justMoved;
  return null;
}

/** Winner by disc count, or null for a draw. */
export function othelloWinner(board: OthelloBoard): Disc | null {
  const { b, w } = countDiscs(board);
  return b === w ? null : b > w ? 'b' : 'w';
}
